import { RootState } from "common/rootReducer";
import React, { useMemo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router";
import { useRazorPay } from "./useRazorPay";
import {
  clearPlaceOrder,
  completePlaceOrder,
  setCartAuthPending,
} from "common/features/cart/cartActions";
import { clearSelectedOffer } from "common/features/offers/offerActions";
import {
  offlinePayment,
  resetOfflinePaymentData,
} from "common/features/payment/PaymentActions";
import {
  getContactlessDineinLandingPage,
  getOrderTrackingURL,
} from "common/utils/route-util";
import {
  OrderType,
  PaymentProviderIdType,
} from "common/features/restaurant/restaurantModels";
import Loadable from "react-loadable";
// page Imports
import Loading from "components/Loading";
import {
  clearGetOrderDetail,
  clearCurrentOrderType,
} from "common/features/order/orderActions";

const FirstDataCardForm = Loadable({
  loader: () => import("pages/payment/FirstData"),
  loading: () => <Loading type="pageLoad" />,
});

const HeartlandForm = Loadable({
  loader: () => import("pages/payment/HeartLand"),
  loading: () => <Loading type="pageLoad" />,
});

const StripePaymentCardForm = Loadable({
  loader: () => import("pages/payment/Stripe/Stripe"),
  loading: () => <Loading type="pageLoad" />,
});

export const usePaymentProviders = (
  totalOrderPrice: number,
  tableId?: string
): [PaymentProviderIdType | null, any, boolean, any] => {
  const history = useHistory();
  const dispatch = useDispatch();

  const [showProviderModal, setShowProviderModal] = useState<boolean>(false);

  const cartOrderId = useSelector((state: RootState) => state.cart.orderId);

  const orderTotal = useSelector(
    (state: RootState) => state.cart.cart.orderTotal
  );

  const placeOrderSuccess = useSelector(
    (state: RootState) => state.cart.placeOrderSuccess
  );
  const placeOrderLoading = useSelector(
    (state: RootState) => state.cart.placeOrderLoading
  );
  const restaurantDetails = useSelector(
    (state: RootState) => state.cart.cartRestaurantDetails
  );
  const selectedRestaurantDetails = useSelector(
    (state: RootState) => state.restaurant.currentRestaurantDetail
  );

  const cartRestaurantSlug = useSelector(
    (state: RootState) => state.cart.cartRestaurantSlug
  );

  const selectedOffer = useSelector(
    (state: RootState) => state.offer.selectedOffer
  );

  const validateCoupanSuccess = useSelector(
    (state: RootState) => state.offer.offerValidSuccess
  );

  const currentOrderType = useSelector(
    (state: RootState) => state.order.currentOrderType
  );

  const offlinePaymentLoading = useSelector(
    (state: RootState) => state.payment.offlinePaymentRequestLoading
  );

  const offlinePaymentSuccess = useSelector(
    (state: RootState) => state.payment.offlinePaymentSuccess
  );

  let { restaurantSlug, menuType } = useParams<any>();

  const isDeliveryOrder = useMemo(() => {
    return (
      selectedRestaurantDetails.orderTypes?.some((orderType) => {
        return orderType.typeName == menuType && orderType.typeGroup == "S";
      }) || false
    );
  }, [selectedRestaurantDetails, menuType]);

  const currentOrderTypeId = useSelector(
    (state: RootState) => state.restaurant.restaurantOrderTypeId
  );

  const restaurantOrderTypes = useSelector(
    (state: RootState) => state.restaurant.currentRestaurantDetail.orderTypes
  );

  const isContactlessDineInOrder = useMemo(() => {
    return (
      window.location.pathname.includes("table") &&
      (selectedRestaurantDetails.dining == 2 ||
        selectedRestaurantDetails.dining == 3)
    );
  }, []);

  const orderId = useSelector((state: RootState) => state.cart.orderId);

  const [payViaRazorPay] = useRazorPay(true, tableId);

  const [isOpened, setIsOpened] = useState(false);

  useEffect(() => {
    if (selectedOffer === null) {
      openPaymentProvider();
    }
  }, [placeOrderSuccess]);

  useEffect(() => {
    if (validateCoupanSuccess && orderId.length > 0) {
      openPaymentProvider();
    }
  }, [validateCoupanSuccess]);

  const restaurantCurrentMenuType = useSelector(
    (state: RootState) => state.restaurant.restaurantCurrentMenuType
  );

  const cartMerchantType = useSelector(
    (state: RootState) => state.cart.merchantType
  );

  useEffect(() => {
    if (
      currentOrderType === "COD" &&
      !offlinePaymentLoading &&
      offlinePaymentSuccess
    ) {
      dispatch(resetOfflinePaymentData());
     // dispatch(clearPlaceOrder());
      dispatch(clearSelectedOffer());
      if (isContactlessDineInOrder) {
        dispatch(clearGetOrderDetail());
        history.push(
          getContactlessDineinLandingPage(
            cartMerchantType,
            cartRestaurantSlug,
            tableId || "",
            restaurantCurrentMenuType
          )
        );
      } else {
        dispatch(clearCurrentOrderType());
        history.replace(getOrderTrackingURL(cartRestaurantSlug, cartOrderId));
      }
    }
  }, [
    currentOrderType,
    offlinePaymentLoading,
    offlinePaymentSuccess,
    restaurantCurrentMenuType,
  ]);

  const openPaymentProvider = () => {
    // If OrderCreated move to Payment
    if (
      !placeOrderLoading &&
      placeOrderSuccess &&
      !isOpened &&
      totalOrderPrice > 0
    ) {
      // Close Cart Pending
      dispatch(setCartAuthPending(false, ""));

      // to prevent invalid navigation, clear success place Order
      setIsOpened(true);
      currentOrderType !== "COD" && setShowProviderModal(true);
      dispatch(completePlaceOrder());

      if (currentOrderType === "COD") {
        const data = {
          orderId: orderId,
          totalPaymentAmount: orderTotal,
          paymentCurrency:
            selectedRestaurantDetails.country === "US" ? "USD" : "INR",
          amountTendered: 0,
          tenderType: isDeliveryOrder ? "POD" : "POS",
        };

        dispatch(offlinePayment(data));
      }
      // Open Payment Provider
      if (
        selectedRestaurantDetails &&
        selectedRestaurantDetails.paymentProvider &&
        !isContactlessDineInOrder &&
        currentOrderType !== "COD"
      ) {
        switch (selectedRestaurantDetails?.paymentProvider.paymentProviderId) {
          case "RAZORPAY_AGGREGATOR":
            payViaRazorPay(totalOrderPrice);
            break;
          case "FIRSTDATA_AGGREGATOR":
            // history.push(
            //   "/restaurant/" +
            //     cartRestaurantSlug +
            //     "/placeOrder/payment/firstData"
            // );
            break;
          case "HEARTLAND_AGGREGATOR":
            // history.push(
            //   "/restaurant/" +
            //     cartRestaurantSlug +
            //     "/placeOrder/payment/heartland"
            // );
            break;
          default:
            break;
        }
      }
    }
  };

  const paymentProvider =
    selectedRestaurantDetails?.paymentProvider?.paymentProviderId || null;

  const PaymentModal = () => {
    if (
      selectedRestaurantDetails &&
      selectedRestaurantDetails.paymentProvider &&
      showProviderModal
      //  &&
      // !isContactlessDineInOrder
    ) {
      switch (selectedRestaurantDetails?.paymentProvider.paymentProviderId) {
        case "RAZORPAY_AGGREGATOR":
          return null;
        case "FIRSTDATA_AGGREGATOR":
          return <FirstDataCardForm />;
        case "HEARTLAND_AGGREGATOR":
          return <HeartlandForm />;
        case "STRIPE_AGGREGATOR":
          return <StripePaymentCardForm />;
        default:
          return null;
      }
    } else {
      return null;
    }
  };

  return [
    paymentProvider,
    PaymentModal,
    showProviderModal,
    setShowProviderModal,
  ];
};
